import Keycloak from "keycloak-js";
import type { TenantBootstrap } from "../tenant/bootstrap.js";

/**
 * ⚠ ADR-003 SPIKE — deliberately disposable.
 *
 * ADR-001 requires a BFF proof-of-concept before the auth ADR is settled, so this exists to PRODUCE EVIDENCE for
 * that decision (reload UX across browsers, refresh coalescing, whether the Unity/kiosk models hold), not to be the
 * final implementation. If ADR-003 lands on a BFF, this is deleted rather than migrated.
 *
 * Hardening applied per rewrite-docs/auth-tenancy.md §5:
 *  - `onLoad: 'login-required'` rather than elaborate silent SSO (this is an authenticated authoring app);
 *  - Authorization Code + **PKCE S256** stated explicitly, not left to the adapter default;
 *  - **`checkLoginIframe: false`** — the login-status iframe depends on third-party cookies, and Keycloak is
 *    cross-SITE to EVERY one of our app origins, in both deployed environments: `my-prod.create.at` /
 *    `my-dev.create.at` (site `create.at`) against `*.arcreator.at` and `kdb-linz.at`. An earlier version of this
 *    note said "*especially* for Takeda", which implied the other four tenants were same-site and merely at risk.
 *    They are not — `arcreator.at` is not `create.at` — and the difference matters: it is why there is no silent
 *    path to a session inside the WebGL panel's frame at all, for anyone, and why ADR-050 acquires one by popup;
 *  - tokens live **in memory only** — never localStorage/sessionStorage, which an XSS could read;
 *  - `realm`/`clientId`/`idpHint` come from the tenant config, **never a URL parameter**.
 */

let keycloak: Keycloak | null = null;
/** In-flight refresh, shared so concurrent requests trigger exactly one token call. */
let refreshInFlight: Promise<void> | null = null;
/** Last adapter-reported auth problem, surfaced to the UI instead of being console-logged. */
let lastAuthError: string | null = null;

export function getAuthError(): string | null {
  return lastAuthError;
}

export interface AuthSession {
  sub: string;
  username: string;
  realm: string;
}

/**
 * Keycloak's base URL. Derived from the app origin in local dev; a real deployment injects it via tenant config.
 *
 * Exported so the WebGL panel's popup flow composes its endpoints from the SAME value the adapter is given
 * (ADR-050 D3). Two sources for "where is Keycloak" is exactly the drift that produces a `redirect_uri` mismatch
 * reading as a credential failure.
 */
export function keycloakBaseUrl(): string {
  return import.meta.env.VITE_KEYCLOAK_URL ?? "http://localhost:8081";
}

/**
 * The login redirect target = the CURRENT deep URL (path + query) with any OAuth callback params stripped, so a
 * deep-linked but unauthenticated visitor (`/?arc_fac_selected=…&arc_facility=…`) returns to EXACTLY that URL after
 * signing in — not the bare root. The registered redirect URI is the wildcard `http://<tenant>:8080/*`, so the query
 * matches. On the callback trip the app's own params ride through Keycloak (appended after `?…&code=…`); stripping the
 * OAuth params here keeps the stored `kc.redirectUri` clean for any later re-login too.
 */
function currentRedirectUri(): string {
  const url = new URL(window.location.href);
  for (const p of ["code", "state", "session_state", "iss", "error", "error_description"]) {
    url.searchParams.delete(p);
  }
  url.hash = "";
  return url.toString();
}

/**
 * Initialise the adapter. MUST run before the router renders — `init()` consumes and rewrites the callback URL,
 * so mounting routes first makes the app briefly interpret the OIDC fragment as application state.
 */
export async function initAuth(
  tenant: TenantBootstrap,
  // Preserve the CURRENT deep URL (path + query, OAuth params stripped) so an unauthenticated deep-link visitor is
  // returned to it after login. The registered redirect URI is the wildcard `http://<tenant>:8080/*` (see the realm
  // config), so the query params match — a bare `${origin}/` was over-cautious and silently dropped the deep link.
  redirectUri: string = currentRedirectUri(),
): Promise<AuthSession | null> {
  if (keycloak) throw new Error("initAuth called twice");

  const kc = new Keycloak({
    url: keycloakBaseUrl(),
    realm: tenant.realm, // from the edge-resolved tenant, never parsed from the hostname
    clientId: tenant.clientId,
  });

  // Callbacks are registered BEFORE init() — afterwards the adapter may already have fired them.
  // (Same ordering as cbook-cockpit's Auth.ts, which is the in-house reference implementation.)
  kc.onAuthError = () => {
    lastAuthError = "authentication failed";
  };
  kc.onAuthRefreshError = () => {
    // The session is gone. Callers must preserve unsaved authoring work before any re-login redirect.
    lastAuthError = "session expired";
  };
  kc.onTokenExpired = () => {
    // Intentionally NOT refreshing here: the transport refreshes on demand, so a backgrounded tab cannot spin
    // on a timer. cbook-cockpit refreshes in this callback because it also pushes tokens to a server session.
  };

  // NO `onLoad: 'login-required'` — deliberately.
  //
  // keycloak-js ignores `idpHint` on that path: its internal `doLogin` builds a fresh `options = {}` and copies
  // ONLY `prompt` and `locale` from initOptions, while `kc_idp_hint` is appended solely from `options.idpHint`
  // (verified in keycloak-js 26.2.4 source). Passing the hint to init() is therefore DEAD CONFIG — Takeda only
  // federates today because its realm pins a default provider, which masks the miss.
  //
  // Initialising bare returns immediately (still consuming an OIDC callback if one is present), which also lets
  // the shell paint branded chrome before any redirect instead of flashing an empty page.
  const authenticated = await kc.init({
    // EXPLICIT redirect URI (cbook-cockpit does the same). Without it the adapter reuses the full current URL
    // including path+query, which is far more likely to miss the registered exact/prefixed redirect URI —
    // and it must be the TENANT origin, never a shared one.
    redirectUri,
    pkceMethod: "S256",
    checkLoginIframe: false,
    responseMode: "query",
    // NOTE the deliberate divergence from cbook-cockpit: it seeds `token`/`refreshToken` from sessionStorage and
    // clears `kcToken`/`kcRefreshToken` on logout. We do NOT persist tokens. auth-tenancy.md §5 requires
    // memory-only, and this codebase's predecessor has a documented XSS/stored-HTML history — persisted tokens
    // would be readable by any injected script. The cost is a top-level redirect on hard reload, which is
    // precisely one of the things this spike is meant to measure.
  });

  keycloak = kc;

  if (!authenticated) {
    // Explicit login is what actually honours the hint (see above). Navigation happens here, so nothing after
    // this resolves in the current document.
    await kc.login({ redirectUri, ...(tenant.idpHint ? { idpHint: tenant.idpHint } : {}) });
    return null;
  }

  const parsed = kc.tokenParsed as { sub?: string; preferred_username?: string } | undefined;
  return {
    sub: parsed?.sub ?? "",
    username: parsed?.preferred_username ?? "",
    realm: tenant.realm,
  };
}

/**
 * Return a valid access token, refreshing if it expires within 30s.
 *
 * Concurrent callers **share one refresh**: without coalescing, a page issuing several requests at once fires
 * several parallel refreshes, and with refresh-token rotation enabled (which ADR-003 requires) the losers present
 * an already-used token and the whole family gets revoked — logging the user out mid-edit.
 */
/**
 * The most recent access token, readable WITHOUT awaiting (ADR-026, diff-panel R6-F5).
 *
 * The unload beacon cannot `await`: `keepalive` only protects a request the unload handler issues SYNCHRONOUSLY, and
 * browsers do not guarantee async continuations run during page teardown. Awaiting `getAccessToken()` (which itself
 * awaits `updateToken`) pushed the fetch into a microtask, so the release was frequently dropped and the lock lingered
 * for the full TTL — precisely the stale lock the beacon exists to prevent.
 */
let lastToken: string | null = null;
export function cachedAccessToken(): string | null {
  return lastToken;
}

export async function getAccessToken(): Promise<string | null> {
  const kc = keycloak;
  if (!kc) return null;

  if (!refreshInFlight) {
    refreshInFlight = kc
      .updateToken(30)
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  try {
    await refreshInFlight;
  } catch {
    // Refresh failed — the session is gone. Callers must preserve unsaved work before this redirects.
    return null;
  }
  // Read the token only AFTER the refresh settles, never a value captured beforehand.
  lastToken = kc.token ?? null; // keep the synchronous mirror current for the unload beacon (R6-F5)
  return lastToken;
}

/**
 * The token for Unity embedded in the editor (ADR-058 D8), from the same coalesced refresh as every other caller. `expiresIn` is
 * the time LEFT now (corrected by the adapter's clock skew), so Unity can count from receipt. `null`: the session is gone.
 */
export async function getEmbedToken(): Promise<{ accessToken: string; expiresIn: number; issuer: string } | null> {
  const accessToken = await getAccessToken();
  const parsed = keycloak?.tokenParsed;
  if (!accessToken || parsed?.exp == null || !parsed.iss) return null;
  const expiresIn = Math.floor(parsed.exp - Date.now() / 1000 + (keycloak?.timeSkew ?? 0));
  return expiresIn > 0 ? { accessToken, expiresIn, issuer: parsed.iss } : null;
}

/**
 * Adopt a session obtained OUTSIDE the adapter — the WebGL panel's popup flow (ADR-050 D3).
 *
 * The panel cannot run `initAuth`: that path ends in `kc.login()`, a whole-document navigation, and the panel is
 * someone else's frame. So the tokens arrive from `signInViaPopup` and are seeded here, which buys the panel the
 * one thing hand-rolling would not: the SAME coalesced `getAccessToken()` refresh seam every other caller uses.
 * Without it the panel would need its own refresh logic, and with refresh-token rotation on (ADR-003) two
 * independent refreshers is how a token family gets revoked mid-edit.
 *
 * **Nothing is persisted.** The tokens are handed to the adapter in memory and never written anywhere — the
 * distinction from the legacy, which put a 365-day refresh token in a script-readable cookie (ADR-050 §2b).
 *
 * **`init` forces an immediate refresh here, and that is wanted.** With `checkLoginIframe: false` and seeded
 * tokens the adapter calls `updateToken(-1)` (`lib/keycloak.js:866`) and RETHROWS on failure when no `onLoad` is
 * set. So a refresh token that Keycloak will not honour fails at sign-in, loudly, instead of on the author's
 * first save.
 */
export async function adoptSession(tenant: TenantBootstrap, tokens: AdoptableTokens): Promise<AuthSession | null> {
  if (keycloak) throw new Error("adoptSession after an adapter already exists");

  const kc = new Keycloak({ url: keycloakBaseUrl(), realm: tenant.realm, clientId: tenant.clientId });
  kc.onAuthError = () => {
    lastAuthError = "authentication failed";
  };
  kc.onAuthRefreshError = () => {
    lastAuthError = "session expired";
  };

  let authenticated: boolean;
  try {
    authenticated = await kc.init({
      token: tokens.token,
      refreshToken: tokens.refreshToken,
      ...(tokens.idToken ? { idToken: tokens.idToken } : {}),
      pkceMethod: "S256",
      checkLoginIframe: false,
      responseMode: "query",
    });
  } catch {
    // The seeded refresh token was refused. NOT an adapter we keep: leaving `keycloak` unset means the caller can
    // offer the sign-in again rather than being stuck behind `adoptSession called twice`.
    lastAuthError = "session expired";
    return null;
  }

  // ORDER MATTERS, and the reverse is what the diff panel caught: assigning the singleton first would leave a
  // DEAD adapter installed while the caller is told it is signed out, so the next attempt hits the guard above and
  // the panel is wedged until a full reload. Unreachable today — with both tokens seeded and no `onLoad` the
  // adapter either throws or returns true — but the catch branch above already promises the opposite, and a
  // red-zone contract that only holds by accident is one refactor away from not holding.
  if (!authenticated) return null;
  keycloak = kc;

  const parsed = kc.tokenParsed as { sub?: string; preferred_username?: string } | undefined;
  return {
    sub: parsed?.sub ?? "",
    username: parsed?.preferred_username ?? "",
    realm: tenant.realm,
  };
}

/** The token trio `adoptSession` accepts. Structural on purpose — it must not import the popup module. */
export interface AdoptableTokens {
  token: string;
  refreshToken: string;
  idToken?: string;
}

/** Explicit re-login, e.g. after a failed refresh. Kept out of components so redirects have one owner. */
export async function login(): Promise<void> {
  await keycloak?.login();
}

export async function logout(): Promise<void> {
  await keycloak?.logout({ redirectUri: `${window.location.origin}/` });
}

/** Test seam: lets the transport be exercised without a real adapter. */
export function __setKeycloakForTests(kc: Keycloak | null): void {
  keycloak = kc;
  refreshInFlight = null;
  lastAuthError = null;
}
